import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarPlus, Sun, Clock, Sparkles, Archive } from 'lucide-react'
import { planTask, type PlanTarget, type PlanTask } from '../../utils/planTask'
import { useToast } from '../ui/Toast'
import { useBranding } from '../../hooks/useBranding'

interface PlanItMenuProps {
  task: PlanTask
  onChanged?: () => void | Promise<void>
  /** Icon-only trigger for list rows. */
  compact?: boolean
  /** Text-link trigger for inline use inside a sentence. */
  asLink?: boolean
  /** Which edge of the trigger the menu hangs from. Links open to the right. */
  align?: 'left' | 'right'
  /** Trigger text (default "Plan it"). */
  label?: string
  className?: string
}

export const PlanItMenu: React.FC<PlanItMenuProps> = ({ task, onChanged, compact = false, asLink = false, align, label = 'Plan it', className = '' }) => {
  const navigate = useNavigate()
  const toast = useToast()
  const { assistant: personaName } = useBranding()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<PlanTarget | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const run = async (target: PlanTarget) => {
    setBusy(target)
    try {
      const result = await planTask(task, target, navigate)
      setOpen(false)
      if (result) {
        toast.toast({ variant: 'success', title: result.title, description: result.description })
        await onChanged?.()
      }
    } catch (err) {
      toast.error('Could not plan it', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(null)
    }
  }

  const items: Array<{ target: PlanTarget; icon: React.ReactNode; label: string; hint: string }> = [
    { target: 'today', icon: <Sun className="h-4 w-4" />, label: 'Today', hint: 'Pin it to today’s focus' },
    { target: 'pick', icon: <Clock className="h-4 w-4" />, label: 'Pick a time', hint: 'Drag it onto the calendar' },
    { target: 'place', icon: <Sparkles className="h-4 w-4" />, label: `Let ${personaName} place it`, hint: 'A flexible slot you can move' },
    { target: 'backlog', icon: <Archive className="h-4 w-4" />, label: 'Backlog', hint: 'Someday; quiet until you come back' },
  ]

  return (
    <div ref={rootRef} className={`relative ${className}`} onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={compact ? `Plan it: ${task.title}` : undefined}
        title="Plan it"
        data-testid="task-plan-it"
        className={asLink
          ? 'inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline'
          : compact
          ? 'inline-flex items-center justify-center min-h-touch min-w-touch rounded-md hover:bg-surface-alt text-muted hover:text-brand transition-colors'
          : 'inline-flex items-center gap-2 rounded-md border border-soft bg-surface min-h-touch px-3 text-sm font-medium text-secondary hover:bg-brand-subtle hover:text-brand transition-colors'}
      >
        {!asLink && <CalendarPlus className="h-4 w-4" />}
        {!compact && <span>{label}</span>}
      </button>
      {open && (
        <div
          role="menu"
          aria-label="Plan it"
          className={`absolute ${(align ?? (asLink ? 'left' : 'right')) === 'left' ? 'left-0' : 'right-0'} top-full z-30 mt-1 w-64 rounded-lg border border-soft bg-surface p-1 shadow-lg`}
        >
          {items.map((it) => (
            <button
              key={it.target}
              role="menuitem"
              type="button"
              disabled={busy !== null}
              onClick={() => run(it.target)}
              data-testid={`task-plan-${it.target}`}
              className="flex w-full items-start gap-3 rounded-md px-3 py-2 text-left hover:bg-surface-alt disabled:opacity-60"
            >
              <span className="mt-0.5 text-brand">{it.icon}</span>
              <span className="min-w-0">
                <span className="block text-sm font-medium text-primary">{it.label}</span>
                <span className="block text-xs text-muted">{it.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default PlanItMenu
